import { motion } from "framer-motion";
import { Puzzle, Users, Sparkles, ArrowRight } from "lucide-react";
import { useCopy } from "@/lib/copyVariant";

const PUZZLE_IMG = "/puzzle-tree.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function PuzzleSection() {
  const { c } = useCopy();
  const FEATURES = [
    { icon: Puzzle,    title: c("puzzleFeature1Title"), body: c("puzzleFeature1Body") },
    { icon: Users,     title: c("puzzleFeature2Title"), body: c("puzzleFeature2Body") },
    { icon: Sparkles,  title: c("puzzleFeature3Title"), body: c("puzzleFeature3Body") },
  ];

  const scrollToJourney = () => {
    document.getElementById("begin-journey")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="puzzle"
      className="relative py-20 sm:py-28 md:py-36 px-5 md:px-10 overflow-hidden bg-[#fff8f3]"
      data-testid="puzzle-section"
    >
      {/* Subtle radial halo */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, rgba(166,193,168,0.18) 0%, transparent 55%)",
        }}
      />

      <div className="max-w-[1280px] mx-auto relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center lg:items-stretch">
        {/* Image - the calm tree formed by 52 cards */}
        <motion.div
          className="relative aspect-[4/3] lg:aspect-auto lg:h-full"
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="puzzle-image-wrapper"
        >
          <div className="relative h-full rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(70,70,70,0.35)] ring-1 ring-[#a6c1a8]/30">
            <img
              src={PUZZLE_IMG}
              alt="All 52 Calmasutras cards arranged together to form a single illustrated Calm Tree"
              className="w-full h-full object-cover object-center block"
              loading="lazy" decoding="async" width="800" height="800"
            />
            {/* Soft inner highlight */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/5 to-white/15" />
          </div>

          {/* Floating tag */}
          <motion.div
            className="absolute -top-4 left-4 sm:-top-6 sm:-left-6 bg-white rounded-full px-4 py-2 shadow-lg ring-1 ring-[#eee5db]"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <p className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase font-semibold text-[#7a9d83]">
              ✦ 52 cards · 1 tree
            </p>
          </motion.div>

          {/* Floating badge bottom-right */}
          <motion.div
            className="hidden sm:flex absolute -bottom-5 -right-4 bg-[#464646] text-[#fff8f3] rounded-full px-4 py-2 shadow-xl items-center gap-2"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.5, type: "spring" }}
          >
            <Puzzle size={14} />
            <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Play & assemble</span>
          </motion.div>
        </motion.div>

        {/* Copy */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          <motion.span
            variants={fadeUp}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-5"
          >
            {c("puzzleEyebrow")}
          </motion.span>

          <motion.h2
            variants={fadeUp}
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-bold text-[#464646] leading-[1.05] tracking-tight mb-6"
          >
            {c("puzzleHeadingL1")}
            <br />
            <span className="italic text-[#7a9d83]">{c("puzzleHeadingL2")}</span>
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="text-base sm:text-lg text-[#464646]/75 leading-relaxed mb-8 max-w-[520px]"
          >
            {c("puzzleBody")}
          </motion.p>

          <motion.ul variants={stagger} className="space-y-4 mb-10">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <motion.li
                key={title}
                variants={fadeUp}
                className="flex gap-4"
                data-testid={`puzzle-feature-${title.replace(/[^a-z]/gi, "").toLowerCase()}`}
              >
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#a6c1a8]/25 flex items-center justify-center mt-0.5">
                  <Icon size={16} className="text-[#7a9d83]" />
                </span>
                <div>
                  <p className="text-sm sm:text-base font-semibold text-[#464646] mb-0.5">{title}</p>
                  <p className="text-sm text-[#464646]/65 leading-relaxed">{body}</p>
                </div>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <motion.button
              onClick={scrollToJourney}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center justify-center gap-2 bg-[#464646] text-[#fff8f3] rounded-full px-7 py-4 text-sm font-semibold tracking-wide shadow-lg shadow-[#464646]/10 hover:shadow-xl hover:shadow-[#464646]/20 transition-shadow"
              data-testid="puzzle-buy-cta"
            >
              {c("puzzleCta")}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </motion.button>
            <p className="text-xs text-[#464646]/50 sm:ml-2">
              Free shipping &nbsp;·&nbsp; ships in 3–5 days
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
